import { useEffect, useState, useCallback, useMemo } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Ban, RotateCcw, Trash2, RefreshCw, Eye, Download, FileSpreadsheet, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import UserDetailsDrawer from "@/components/admin/UserDetailsDrawer";
import { exportCSV, exportExcel } from "@/lib/exporters";

const ROLES = ["", "professional", "organization", "institution", "verifier", "super_admin"];

const EXPORT_COLUMNS = [
  { key: "full_name", label: "Name", value: (u) => u.full_name || u.organization_name || "" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "ethic_id", label: "EthicCred ID", value: (u) => u.ethic_id || u.org_id || u.institution_id || "" },
  { key: "country", label: "Country" },
  { key: "status", label: "Status", value: (u) => (u.suspended ? "Suspended" : "Active") },
  { key: "mfa_enabled", label: "MFA", value: (u) => (u.mfa_enabled ? "Yes" : "No") },
  { key: "created_at", label: "Joined", value: (u) => (u.created_at || "").slice(0, 10) },
];

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [drawerUid, setDrawerUid] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { q, role, status_filter: statusFilter, skip, limit } });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      /* errors surfaced via toast at action sites */
    }
  }, [q, role, statusFilter, skip]);

  useEffect(() => { load(); }, [load]);

  const handleSetQ = (val) => { setQ(val); setSkip(0); };
  const handleSetRole = (val) => { setRole(val); setSkip(0); };
  const handleSetStatus = (val) => { setStatusFilter(val); setSkip(0); };

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const av = (a[sortKey] || a.organization_name || "").toString().toLowerCase();
      const bv = (b[sortKey] || b.organization_name || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const act = async (uid, fn) => {
    try {
      await fn();
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fetchAllForExport = async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { q, role, status_filter: statusFilter, skip: 0, limit: 5000 } });
      return data.rows;
    } catch {
      toast.error("Failed to fetch export rows");
      return [];
    }
  };

  const handleExportCSV = async () => {
    const all = await fetchAllForExport();
    if (all.length === 0) return;
    exportCSV(`ethiccred-users-${Date.now()}`, all, EXPORT_COLUMNS);
    toast.success(`Exported ${all.length} rows (CSV)`);
  };
  const handleExportExcel = async () => {
    const all = await fetchAllForExport();
    if (all.length === 0) return;
    exportExcel(`ethiccred-users-${Date.now()}`, all, EXPORT_COLUMNS, "Users");
    toast.success(`Exported ${all.length} rows (Excel)`);
  };

  return (
    <AdminLayout subtitle="Module 2" title="User Management" actions={
      <div className="flex items-center gap-3">
        <div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} users</div>
        <Button size="sm" variant="outline" onClick={handleExportCSV} className="rounded-sm h-9 border-white/10 text-white hover:bg-white/10 gap-2" data-testid="users-export-csv">
          <Download className="w-3.5 h-3.5" />CSV
        </Button>
        <Button size="sm" variant="outline" onClick={handleExportExcel} className="rounded-sm h-9 border-white/10 text-white hover:bg-white/10 gap-2" data-testid="users-export-excel">
          <FileSpreadsheet className="w-3.5 h-3.5" />Excel
        </Button>
      </div>
    }>
      <Filters q={q} setQ={handleSetQ} role={role} setRole={handleSetRole} statusFilter={statusFilter} setStatusFilter={handleSetStatus} testid="users-filters" />

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <SortableTh sortKey="full_name" current={sortKey} dir={sortDir} onClick={toggleSort} testid="users-th-name">User</SortableTh>
                <SortableTh sortKey="role" current={sortKey} dir={sortDir} onClick={toggleSort} testid="users-th-role">Role</SortableTh>
                <Th>EthicCred ID</Th>
                <SortableTh sortKey="country" current={sortKey} dir={sortDir} onClick={toggleSort} testid="users-th-country">Country</SortableTh>
                <Th>Status</Th>
                <SortableTh sortKey="created_at" current={sortKey} dir={sortDir} onClick={toggleSort} testid="users-th-joined">Joined</SortableTh>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`user-row-${u.id}`}>
                  <Td>
                    <div className="font-semibold text-white">{u.full_name || u.organization_name || "—"}</div>
                    <div className="text-xs text-zinc-500">{u.email}</div>
                  </Td>
                  <Td><Badge className="bg-white/10 border-white/10 rounded-sm">{u.role}</Badge></Td>
                  <Td className="font-mono-plex text-xs text-zinc-400">{u.ethic_id || u.org_id || u.institution_id || "—"}</Td>
                  <Td className="text-zinc-400">{u.country || "—"}</Td>
                  <Td>
                    {u.suspended
                      ? <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Suspended</Badge>
                      : <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm">Active</Badge>}
                  </Td>
                  <Td className="text-xs text-zinc-500 font-mono-plex">{(u.created_at || "").slice(0, 10)}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <IconBtn title="View details" onClick={() => setDrawerUid(u.id)} testid={`user-view-${u.id}`}>
                        <Eye className="w-3 h-3" />
                      </IconBtn>
                      {u.suspended ? (
                        <IconBtn title="Restore" onClick={() => act(u.id, () => api.post(`/admin/users/${u.id}/restore`))} testid={`user-restore-${u.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </IconBtn>
                      ) : (
                        <IconBtn title="Suspend" onClick={() => {
                          const reason = prompt("Reason for suspension:") || "";
                          act(u.id, () => api.post(`/admin/users/${u.id}/suspend`, { reason }));
                        }} testid={`user-suspend-${u.id}`}>
                          <Ban className="w-3 h-3" />
                        </IconBtn>
                      )}
                      <IconBtn title="Reset score" onClick={() => act(u.id, () => api.post(`/admin/users/${u.id}/reset-score`))} testid={`user-reset-${u.id}`}>
                        <RefreshCw className="w-3 h-3" />
                      </IconBtn>
                      {u.role !== "super_admin" && (
                        <IconBtn title="Delete" danger onClick={() => {
                          if (confirm(`Permanently delete ${u.email}? This cannot be undone.`)) {
                            act(u.id, () => api.delete(`/admin/users/${u.id}`));
                          }
                        }} testid={`user-delete-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </IconBtn>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500" data-testid="users-empty">No users.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />

      {drawerUid && <UserDetailsDrawer userId={drawerUid} onClose={() => setDrawerUid(null)} />}
    </AdminLayout>
  );
}

export function Filters({ q, setQ, role, setRole, statusFilter, setStatusFilter, testid }) {
  return (
    <div className="flex flex-wrap gap-3 mb-5" data-testid={testid}>
      <div className="relative flex-1 min-w-[240px]">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name, EthicCred ID…"
          className="rounded-sm h-9 pl-9 bg-white/5 border-white/10 text-white"
          data-testid={`${testid}-search`}
        />
      </div>
      {setRole && (
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm"
          data-testid={`${testid}-role`}
        >
          {ROLES.map((r) => <option key={r} value={r} className="bg-ec-ink">{r || "All roles"}</option>)}
        </select>
      )}
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm"
        data-testid={`${testid}-status`}
      >
        <option value="" className="bg-ec-ink">All statuses</option>
        <option value="active" className="bg-ec-ink">Active</option>
        <option value="suspended" className="bg-ec-ink">Suspended</option>
      </select>
    </div>
  );
}

export function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 eyebrow text-zinc-500 ${className}`}>{children}</th>;
}
export function SortableTh({ children, sortKey, current, dir, onClick, testid, className = "" }) {
  const active = current === sortKey;
  return (
    <th className={`px-4 py-3 eyebrow text-zinc-500 ${className}`}>
      <button onClick={() => onClick(sortKey)} className={`flex items-center gap-1 ${active ? "text-white" : "hover:text-white"}`} data-testid={testid}>
        {children}
        {active && (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </button>
    </th>
  );
}
export function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle text-zinc-200 ${className}`}>{children}</td>;
}
export function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-sm border transition-colors ${danger ? "border-ec-error/30 text-ec-error hover:bg-ec-error/10" : "border-white/10 text-zinc-300 hover:bg-white/10"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
export function Pagination({ total, skip, limit, setSkip }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between mt-5 text-xs text-zinc-500 font-mono-plex">
      <span>Page {page} / {pages}</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-white hover:bg-white/10" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="pagination-prev">Prev</Button>
        <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-white hover:bg-white/10" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="pagination-next">Next</Button>
      </div>
    </div>
  );
}
